/**
 * The organization's connector list as the agent editor's Connectors tab
 * sees it: the EFFECTIVE list of the agent's organization (its own entries
 * and its ancestors', with every level's changes applied), which of them this
 * agent already has, and whether the person editing may manage the list.
 *
 * Loaded once per tab and shared by the MCP and API sections; their "Add"
 * stays disabled until it answers. A failure to load is shown, not hidden:
 * the Add dialog says the list could not be loaded, offers a retry, and the
 * custom form stays right there.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFeature } from '@hooks/useFeature';
import { useTenantPath } from '@/routing';
import {
  connectorLibraryService,
  manageLibraryPath,
  type PickerEntry,
} from '../services/connectorLibraryService';

export interface AgentConnectorLibrary {
  loaded: boolean;
  /** The picker could not be loaded. */
  failed: boolean;
  entries: PickerEntry[];
  canManage: boolean;
  /** The organization whose list a manager would open from here. */
  manageTenantId: string | null;
  /** The list page a manager would open from here, or null. */
  manageHref: string | null;
  reload: () => Promise<void>;
}

export function useAgentConnectorLibrary(chatbotId?: string): AgentConnectorLibrary {
  const mcpOffered = useFeature('integrations.mcp_connectors');
  const apiOffered = useFeature('integrations.api_connectors');
  const [entries, setEntries] = useState<PickerEntry[]>([]);
  const [canManage, setCanManage] = useState(false);
  const [manageTenantId, setManageTenantId] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const tenantPath = useTenantPath();
  // Only the latest request may write: a slower answer for another agent
  // must never land on this one.
  const latest = useRef(0);

  // Another agent: nothing of the previous one's library stays on screen.
  useEffect(() => {
    setEntries([]);
    setCanManage(false);
    setManageTenantId(null);
    setFailed(false);
    setLoaded(false);
  }, [chatbotId]);

  const reload = useCallback(async () => {
    const request = ++latest.current;
    if (!chatbotId || (!mcpOffered && !apiOffered)) { setLoaded(true); return; }
    try {
      const result = await connectorLibraryService.picker(chatbotId);
      if (request !== latest.current) return;
      setEntries(result.entries);
      setCanManage(result.can_manage);
      setManageTenantId(result.manage_tenant_id);
      setFailed(false);
    } catch (err) {
      if (request !== latest.current) return;
      console.error('Connector list failed to load', err);
      setFailed(true);
    }
    setLoaded(true);
  }, [chatbotId, mcpOffered, apiOffered]);

  useEffect(() => { void reload(); }, [reload]);

  const manageHref = canManage && manageTenantId ? tenantPath(manageLibraryPath(manageTenantId)) : null;

  return useMemo(() => ({ loaded, failed, entries, canManage, manageTenantId, manageHref, reload }),
    [loaded, failed, entries, canManage, manageTenantId, manageHref, reload]);
}
